import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import TodoListCard from './components/TodoListCard'
import TodoListFormDialog from './components/TodoListFormDialog'
import TodoShareDialog from './components/TodoShareDialog'
import { useTodoLists } from './hooks/useTodoLists'
import { useProxyMode } from '../proxy/useProxyMode'
import type { TodoList, TodoListUpsert } from './types'
import './todo.css'
// Paylaşılan form/düzen sınıfları (admin-form vb.).
import '../admin/admin.css'

/**
 * Liste silme onayının <b>grubu</b> — aşağıdaki {@code <ConfirmDialog group=…>} örneğiyle eşleşir.
 *
 * <p><b>Neden grup şart:</b> {@code confirmDialog()} bir servis çağrısıdır ve mount edilmiş
 * <b>tüm</b> {@code <ConfirmDialog>} örneklerine yayın yapar. Bu sayfa, kendi örneğinin yanı sıra
 * {@code TodoShareDialog}'u da render ediyor ve onun da bir örneği var; ikisi de grupsuzken paylayım
 * pop-up'ı açıkken verilen bir onay çağrısı <b>iki</b> pencere açıyordu (kullanıcı bulgusu
 * 26.09.2026).
 */
const LIST_DELETE_GROUP = 'todo-list-delete'

/**
 * <b>Notlarım</b> ana ekranı (113) — kullanıcının kendi listeleri ve kendisiyle paylaşılanlar.
 *
 * <p>Gösterim <b>kart listesidir</b>, tablo değil: her kartın taşıdığı bilgi azdır (ad, sayaç, sahip)
 * ve asıl eylem karta girmektir; tablo burada satır başına boş kolonlar üretirdi. Kart kalıbı
 * paylaşılan `ui-card` iskeletini kullanır, ekrana özel bir kart bileşeni yazılmaz.
 *
 * <p>Hangi listeyi göreceğine <b>sunucu</b> karar verir (kendi + kendisiyle paylaşılanlar); ekran
 * ayrıca bir süzme yapmaz — iki yerde iki ölçüt olması, biri güncellendiğinde diğerinin bayatlaması
 * demekti.
 */
export default function TodoListsPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { list, create, update, remove } = useTodoLists()
  /**
   * <b>Kısıtlı Yetkili asistan Notlarım'ı GÖRÜR ama yazamaz</b> (kullanıcı bildirimi 21.10.2026:
   * *"notlarım kısmı kısıtlı yetkide neden gizleniyo? sadece read only olmalıydı"*).
   *
   * <p>Bir tur önce modül, yetki kodu izin listesinden çıkarılarak tamamen gizlenmişti — bu modülde
   * 14 mutasyonun hepsi tek kodun arkasında olduğu için kod açıkken arayüz çalışmayan düğmeler
   * çiziyordu. Doğru çare Ajanda'dakiyle aynı: kod kalır, yazma yüzeylerini **arayüz** kapatır.
   */
  const { readOnly } = useProxyMode()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<TodoList | null>(null)
  /** Paylaşımı açık liste; `null` → pop-up kapalı. Kimliği tutmak yerine kaydın kendisi tutulur:
      pop-up başlığında liste adı da gösteriliyor. */
  const [sharing, setSharing] = useState<TodoList | null>(null)

  const rows = list.data ?? []
  const busyId = remove.isPending ? remove.variables : null

  const openNew = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openRename = (row: TodoList) => {
    setEditing(row)
    setFormOpen(true)
  }

  const submit = (payload: TodoListUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: payload },
        {
          onSuccess: () => {
            toast.success(t('todo.updated'))
            setFormOpen(false)
          },
        },
      )
      return
    }
    create.mutate(payload, {
      onSuccess: () => {
        toast.success(t('todo.created'))
        setFormOpen(false)
      },
    })
  }

  const confirmDelete = (row: TodoList) =>
    confirmDialog({
      group: LIST_DELETE_GROUP,
      /* Başkasının listesini silmek AYRI bir uyarı metni alır (push kapısı bulgusu 30.09.2026):
         30.09.2026'dan beri paylaşılan kullanıcı da silebiliyor ve işlem liste SAHİBİNİN ekranından
         da kaldırıyor, geri alma yolu da yok (çöp kutusu spec'te Out of Scope). Tek tip metin,
         kullanıcının yalnız kendi kaydını sildiğini sanmasına yol açardı. */
      message: row.owned
        ? t('todo.deleteListConfirm', { name: row.name })
        : t('todo.deleteSharedListConfirm', {
            name: row.name,
            owner: row.ownerFullName ?? '',
          }),
      header: t('todo.deleteList'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      rejectLabel: t('common.cancel'),
      accept: () =>
        remove.mutate(row.id, { onSuccess: () => toast.success(t('todo.deleted')) }),
    })

  return (
    <div className="admin-page">
      {/* `group` ZORUNLU — bkz. LIST_DELETE_GROUP. */}
      <ConfirmDialog group={LIST_DELETE_GROUP} />
      <PageHeader
        title={t('todo.title')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            {!readOnly && (
              <Button icon="pi pi-plus" label={t('todo.newList')} onClick={openNew} />
            )}
          </>
        }
      />

      {/* Açıklama salt-okumada DÜŞER: metin bir EYLEM ÇAĞRISIDIR ("… ile oluşturun") ve o
          kipte çağırdığı düğme çizilmiyor. Başlık bilgiyi zaten taşıyor, yani düşürmekle bir şey
          kaybedilmiyor — bırakmak, olmayan bir yüzeyi tarif ederek tam da FR-019'un kapatmaya
          çalıştığı yanılgıyı üretirdi (push kapısı review'ü 21.10.2026). */}
      {!list.isLoading && rows.length === 0 && (
        <EmptyState
          title={t('todo.emptyTitle')}
          description={readOnly ? undefined : t('todo.emptyDesc')}
        />
      )}

      <div className="ui-card-list todo-list-grid">
        {list.isLoading &&
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
          ))}
        {!list.isLoading &&
          rows.map((row) => (
            <TodoListCard
              key={row.id}
              list={row}
              busy={busyId === row.id}
              readOnly={readOnly}
              onRename={openRename}
              onShare={setSharing}
              onDelete={confirmDelete}
            />
          ))}
      </div>

      <TodoShareDialog list={sharing} onHide={() => setSharing(null)} />

      <TodoListFormDialog
        visible={formOpen}
        initial={editing}
        submitting={create.isPending || update.isPending}
        onSubmit={submit}
        onHide={() => setFormOpen(false)}
      />
    </div>
  )
}
